import { FC, useCallback, useRef } from 'react'
import { GeoJSON, MapContainer, TileLayer, useMapEvents } from 'react-leaflet'
import { Feature, GeoJsonObject } from 'geojson'
import { MAP_DATA } from './MapData'
import { DashboardMap } from '../../../types/dashboard.type'
import styles from './styles.module.scss'

const COLORS = {
  red: ['#e31a1c', '#e31a1c'],
  yellow: ['#fecc5c', '#fe8c3c'],
  green: ['#31a354', '#31a354'],
  blue: ['#3194d3', '#3194d3'],
}

const defaultStyle = {
  weight: 1.2,
  opacity: 0.4,
  fillOpacity: 0.25,
}

const highlightStyle = {
  weight: 2,
  fillOpacity: 0.3,
  opacity: 1,
}

function MapClickHandler({ onMapClick }: { onMapClick: () => void }) {
  useMapEvents({
    click: () => {
      onMapClick()
    },
  })

  return null
}

const Map: FC<DashboardMap> = ({ shapes, highlight }) => {
  const selectedRef = useRef<L.Path | null>(null)

  const handleFeatureAdd = useCallback((feature: Feature, layer: L.Layer) => {
    const typedLayer = layer as L.Path

    const popupContent = `<p>ULS ID: ${feature.properties?.ULS_ID}<br>MARKET: ${feature.properties?.MARKET}</p>`
    layer.bindPopup(popupContent)

    layer.on('click', function (e) {
      if (selectedRef.current) {
        selectedRef.current.setStyle(defaultStyle)

        if (selectedRef.current === layer) {
          selectedRef.current = null
          layer.closePopup()
          return
        }
      }

      typedLayer.setStyle(highlightStyle)
      typedLayer.bringToFront()

      selectedRef.current = typedLayer
    })
  }, [])

  const handleMapClick = useCallback(() => {
    if (selectedRef.current) {
      selectedRef.current.setStyle(defaultStyle)
      selectedRef.current = null
    }
  }, [])

  return (
    <MapContainer
      style={{ width: '100%', height: '100%', outline: 'none', borderRadius: '4px' }}
      zoom={7}
      minZoom={3}
      attributionControl={false}
      center={[38, -78.5]}
      worldCopyJump={false}
      maxBoundsViscosity={1.0}
      maxBounds={[
        [-90, -180],
        [90, 180],
      ]}
    >
      {/*
    <TileLayer
      url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      detectRetina={true}
      maxNativeZoom={18}
      maxZoom={19} // 18+1 because of the detectRetina=true
    />

    <TileLayer noWrap url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" />
    */}

      <TileLayer noWrap url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" opacity={0.6} />

      <MapClickHandler onMapClick={handleMapClick} />

      <GeoJSON
        key={JSON.stringify(highlight)}
        data={MAP_DATA as GeoJsonObject}
        onEachFeature={handleFeatureAdd}
        style={(feature) => {
          const ulsId = feature?.properties?.ULS_ID
          let color = COLORS.green

          if (highlight?.blue?.includes(ulsId) || highlight?.blue?.includes('all')) {
            color = COLORS.blue
          }

          if (highlight?.yellow?.includes(ulsId) || highlight?.yellow?.includes('all')) {
            color = COLORS.yellow
          }

          if (highlight?.red?.includes(ulsId) || highlight?.red?.includes('all')) {
            color = COLORS.red
          }

          return {
            ...defaultStyle,
            color: color[1],
            fillColor: color[0],
          }
        }}
      />

      {shapes?.map((s, i) => (
        <GeoJSON
          key={i}
          data={s.data}
          style={s.style}
          onEachFeature={(feature, layer) => {
            if (s.label) {
              layer.bindTooltip(typeof s.label === 'string' ? s.label : s.label?.(feature), {
                permanent: true,
                direction: 'center',
                className: styles.leafletLabelTooltip + ' ' + styles[`leafletLabelTooltip_${s.labelColor}`],
              })
            }
          }}
        />
      ))}
    </MapContainer>
  )
}

export default Map
